import React, { useCallback, useState, createContext, useContext } from 'react';
type Lang = 'en' | 'de' | 'es';
interface LanguageContextType {
  lang: Lang;
  toggleLang: () => void;
  t: (key: string) => string;
}
const translations: Record<string, Record<Lang, string>> = {
  // Navbar
  'nav.team': {
    en: 'Team',
    de: 'Mannschaft',
    es: 'Equipo'
  },
  'nav.matches': {
    en: 'Matches',
    de: 'Spiele',
    es: 'Partidos'
  },
  'nav.deals': {
    en: 'Deals',
    de: 'Angebote',
    es: 'Ofertas'
  },
  'nav.liveScores': {
    en: 'Live Scores',
    de: 'Live-Ergebnisse',
    es: 'Resultados en Vivo'
  },
  'nav.orderYours': {
    en: 'Order Yours',
    de: 'Jetzt Bestellen',
    es: 'Pide la Tuya'
  },
  // Hero
  'hero.badge': {
    en: "You're now part of Germany 2026",
    de: 'Du bist jetzt Teil von Deutschland 2026',
    es: 'Ahora eres parte de Alemania 2026'
  },
  'hero.title1': {
    en: "Don't Just Support Germany,",
    de: 'Unterstütze Deutschland nicht nur,',
    es: 'No solo apoyes a Alemania,'
  },
  'hero.title2': {
    en: 'Live It',
    de: 'Lebe Es',
    es: 'Vívelo'
  },
  'hero.subtitle': {
    en: 'Updated in real live time. Access exclusive content, match updates, and travel deals directly from your digital hub.',
    de: 'In Echtzeit aktualisiert. Greife direkt von deinem digitalen Hub auf exklusive Inhalte, Spiel-Updates und Reiseangebote zu.',
    es: 'Actualizado en tiempo real. Accede a contenido exclusivo, actualizaciones de partidos y ofertas de viaje directamente desde tu hub digital.'
  },
  'hero.cta1': {
    en: 'Watch Matches',
    de: 'Spiele Ansehen',
    es: 'Ver Partidos'
  },
  'hero.cta2': {
    en: 'Explore Team',
    de: 'Team Entdecken',
    es: 'Explorar Equipo'
  },
  'hero.scroll': {
    en: 'Scroll to discover',
    de: 'Scrollen zum Entdecken',
    es: 'Desplázate para descubrir'
  },
  // Team Overview
  'team.label': {
    en: 'Our Legacy',
    de: 'Unser Vermächtnis',
    es: 'Nuestro Legado'
  },
  'team.title1': {
    en: 'A Nation Forged',
    de: 'Eine Nation geschmiedet',
    es: 'Una Nación Forjada'
  },
  'team.title2': {
    en: 'In Glory',
    de: 'in Ruhm',
    es: 'en la Gloria'
  },
  'team.desc': {
    en: 'The German national team represents more than just football; it embodies the passion, resilience, and tactical brilliance of a nation. From the Miracle of Bern to conquering the world in Brazil, Die Mannschaft continues to write its name in the stars.',
    de: 'Die deutsche Nationalmannschaft repräsentiert mehr als nur Fußball; sie verkörpert die Leidenschaft, Widerstandsfähigkeit und taktische Brillanz einer Nation. Vom Wunder von Bern bis zur Weltmeisterschaft in Brasilien schreibt Die Mannschaft ihren Namen weiterhin in die Sterne.',
    es: 'La selección alemana representa más que solo fútbol; encarna la pasión, la resiliencia y la brillantez táctica de una nación. Desde el Milagro de Berna hasta la conquista del mundo en Brasil, Die Mannschaft sigue escribiendo su nombre en las estrellas.'
  },
  'team.since': {
    en: 'Since 1908',
    de: 'Seit 1908',
    es: 'Desde 1908'
  },
  'team.history': {
    en: 'Explore Our Full History →',
    de: 'Entdecke unsere ganze Geschichte →',
    es: 'Explora Nuestra Historia Completa →'
  },
  'team.wc': {
    en: '4 World Cups',
    de: '4 Weltmeisterschaften',
    es: '4 Copas del Mundo'
  },
  'team.wcDesc': {
    en: '1954, 1974, 1990, 2014',
    de: '1954, 1974, 1990, 2014',
    es: '1954, 1974, 1990, 2014'
  },
  'team.euro': {
    en: '3 European Titles',
    de: '3 Europameisterschaften',
    es: '3 Títulos Europeos'
  },
  'team.euroDesc': {
    en: '1972, 1980, 1996',
    de: '1972, 1980, 1996',
    es: '1972, 1980, 1996'
  },
  'team.confed': {
    en: 'Confed Cup',
    de: 'Confed Cup',
    es: 'Copa Confed'
  },
  'team.confedDesc': {
    en: '2017 Champions',
    de: 'Sieger 2017',
    es: 'Campeones 2017'
  },
  'team.appearances': {
    en: '20 World Cups',
    de: '20 Weltmeisterschaften',
    es: '20 Copas del Mundo'
  },
  'team.appearancesDesc': {
    en: 'Global Stage Appearances',
    de: 'Teilnahmen auf der Weltbühne',
    es: 'Apariciones en el Escenario Global'
  },
  'team.videoLabel': {
    en: 'Die Mannschaft',
    de: 'Die Mannschaft',
    es: 'Die Mannschaft'
  },
  'team.videoTitle1': {
    en: 'The War',
    de: 'Die Kriegs',
    es: 'La Máquina'
  },
  'team.videoTitle2': {
    en: 'Machine',
    de: 'Maschine',
    es: 'de Guerra'
  },
  // Key Players
  'players.label': {
    en: 'The Squad',
    de: 'Der Kader',
    es: 'La Plantilla'
  },
  'players.title1': {
    en: 'The Stars of',
    de: 'Die Sterne von',
    es: 'Las Estrellas de'
  },
  'players.title2': {
    en: 'Germany',
    de: 'Deutschland',
    es: 'Alemania'
  },
  'players.viewSquad': {
    en: 'View Full Squad on DFB.de',
    de: 'Gesamten Kader auf DFB.de ansehen',
    es: 'Ver Plantilla Completa en DFB.de'
  },
  'players.forward': {
    en: 'Forward',
    de: 'Stürmer',
    es: 'Delantero'
  },
  'players.midfielder': {
    en: 'Midfielder',
    de: 'Mittelfeldspieler',
    es: 'Centrocampista'
  },
  'players.defender': {
    en: 'Defender',
    de: 'Verteidiger',
    es: 'Defensa'
  },
  // Match Center
  'matches.label': {
    en: 'World Cup 2026',
    de: 'Weltmeisterschaft 2026',
    es: 'Mundial 2026'
  },
  'matches.title1': {
    en: 'Match',
    de: 'Match',
    es: 'Centro de'
  },
  'matches.title2': {
    en: 'Center',
    de: 'Center',
    es: 'Partidos'
  },
  'matches.desc': {
    en: 'Follow Die Mannschaft in Group E. Get live updates, official lineups, and real-time scores from the biggest stage in football.',
    de: 'Verfolge Die Mannschaft in Gruppe E. Erhalte Live-Updates, offizielle Aufstellungen und Echtzeit-Ergebnisse von der größten Bühne des Fußballs.',
    es: 'Sigue a Die Mannschaft en el Grupo E. Obtén actualizaciones en vivo, alineaciones oficiales y resultados en tiempo real del mayor escenario del fútbol.'
  },
  'matches.standings': {
    en: 'Group Standings',
    de: 'Gruppenplatzierung',
    es: 'Clasificación del Grupo'
  },
  'matches.live': {
    en: 'Live',
    de: 'Live',
    es: 'En Vivo'
  },
  'matches.autoRefresh': {
    en: 'Auto-refreshes every 5 min',
    de: 'Aktualisiert sich alle 5 Min',
    es: 'Se actualiza cada 5 min'
  },
  'matches.refreshNow': {
    en: 'Refresh Now',
    de: 'Jetzt Aktualisieren',
    es: 'Actualizar Ahora'
  },
  'matches.team': {
    en: 'Team',
    de: 'Mannschaft',
    es: 'Equipo'
  },
  'matches.viewStandings': {
    en: 'View Live Standings on FlashScore',
    de: 'Live-Tabelle auf FlashScore ansehen',
    es: 'Ver Clasificación en Vivo en FlashScore'
  },
  'matches.nextMatch': {
    en: 'Next Match',
    de: 'Nächstes Spiel',
    es: 'Próximo Partido'
  },
  'matches.espn': {
    en: 'ESPN Schedule',
    de: 'ESPN Spielplan',
    es: 'Calendario ESPN'
  },
  'matches.sofascore': {
    en: 'SofaScore',
    de: 'SofaScore',
    es: 'SofaScore'
  },
  'matches.flashscore': {
    en: 'FlashScore Live',
    de: 'FlashScore Live',
    es: 'FlashScore en Vivo'
  },
  'matches.foxsports': {
    en: 'Fox Sports',
    de: 'Fox Sports',
    es: 'Fox Sports'
  },
  // Stats
  'stats.label': {
    en: 'Germany By The Numbers',
    de: 'Deutschland in Zahlen',
    es: 'Alemania en Números'
  },
  'stats.worldCup': {
    en: 'World Cup Appearances',
    de: 'WM-Teilnahmen',
    es: 'Apariciones en Mundiales'
  },
  'stats.titles': {
    en: 'Major Titles',
    de: 'Große Titel',
    es: 'Títulos Principales'
  },
  'stats.goals': {
    en: 'International Goals',
    de: 'Länderspieltore',
    es: 'Goles Internacionales'
  },
  'stats.klose': {
    en: 'Klose Goals (Record)',
    de: 'Klose Tore (Rekord)',
    es: 'Goles de Klose (Récord)'
  },
  // Historic Moments
  'history.label': {
    en: 'The Archives',
    de: 'Die Archive',
    es: 'Los Archivos'
  },
  'history.title1': {
    en: 'Moments That',
    de: 'Momente, die',
    es: 'Momentos Que'
  },
  'history.title2': {
    en: 'Made History',
    de: 'Geschichte schrieben',
    es: 'Hicieron Historia'
  },
  'history.wc2014': {
    en: '2014 World Cup Final',
    de: 'WM-Finale 2014',
    es: 'Final del Mundial 2014'
  },
  'history.bestGoals': {
    en: "Germany's Best FIFA World Cup Goals",
    de: 'Deutschlands beste FIFA WM-Tore',
    es: 'Los Mejores Goles de Alemania en Mundiales FIFA'
  },
  'history.wc1990': {
    en: '1990 World Cup Triumph',
    de: 'WM-Triumph 1990',
    es: 'Triunfo en el Mundial 1990'
  },
  'history.bern1954': {
    en: 'The Miracle of Bern 1954',
    de: 'Das Wunder von Bern 1954',
    es: 'El Milagro de Berna 1954'
  },
  'history.euro1996': {
    en: 'Euro 1996 Golden Goal',
    de: 'Euro 1996 Golden Goal',
    es: 'Gol de Oro Euro 1996'
  },
  'history.wc1974': {
    en: '1974 World Cup Final',
    de: 'WM-Finale 1974',
    es: 'Final del Mundial 1974'
  },
  'history.kloseGoals': {
    en: 'Miroslav Klose FIFA World Cup Goals',
    de: 'Miroslav Klose FIFA WM-Tore',
    es: 'Goles de Miroslav Klose en Mundiales FIFA'
  },
  'history.mineirazo': {
    en: 'The Mineirazo',
    de: 'Das Mineirazo',
    es: 'El Mineirazo'
  },
  'history.gerdMuller': {
    en: 'Gerd Müller Every FIFA World Cup Goal',
    de: 'Gerd Müller Alle FIFA WM-Tore',
    es: 'Gerd Müller Todos los Goles en Mundiales FIFA'
  },
  'history.neuerSaves': {
    en: 'Manuel Neuer FIFA World Cup Saves',
    de: 'Manuel Neuer FIFA WM-Paraden',
    es: 'Atajadas de Manuel Neuer en Mundiales FIFA'
  },
  // Fan Experience
  'fan.label': {
    en: 'More Than A Game',
    de: 'Mehr als ein Spiel',
    es: 'Más Que Un Juego'
  },
  'fan.title1': {
    en: 'Feel the Passion of',
    de: 'Fühle die Leidenschaft von',
    es: 'Siente la Pasión de'
  },
  'fan.title2': {
    en: 'Germany',
    de: 'Deutschland',
    es: 'Alemania'
  },
  'fan.desc': {
    en: 'Join millions of voices singing as one. Wear the colors, feel the history, and be part of the journey to global glory.',
    de: 'Schließe dich Millionen von Stimmen an, die wie eine singen. Trage die Farben, fühle die Geschichte und sei Teil der Reise zum weltweiten Ruhm.',
    es: 'Únete a millones de voces cantando como una sola. Viste los colores, siente la historia y sé parte del viaje hacia la gloria mundial.'
  },
  // Tickets / Travel Deals
  'deals.label': {
    en: 'Travel to the World Cup',
    de: 'Reise zur Weltmeisterschaft',
    es: 'Viaja al Mundial'
  },
  'deals.title1': {
    en: 'Exclusive',
    de: 'Exklusive',
    es: 'Ofertas Exclusivas'
  },
  'deals.title2': {
    en: 'Fan Deals',
    de: 'Fan-Angebote',
    es: 'para Fans'
  },
  'deals.desc': {
    en: 'Exclusive discounts for Germany fans on tickets, hotels, flights & transport to support Die Mannschaft in North America.',
    de: 'Exklusive Rabatte für Deutschland-Fans auf Tickets, Hotels, Flüge & Transport, um Die Mannschaft in Nordamerika zu unterstützen.',
    es: 'Descuentos exclusivos para fans de Alemania en entradas, hoteles, vuelos y transporte para apoyar a Die Mannschaft en Norteamérica.'
  },
  'deals.unlockVip': {
    en: 'Unlock VIP Discounts',
    de: 'VIP-Rabatte freischalten',
    es: 'Desbloquea Descuentos VIP'
  },
  'deals.applyCode': {
    en: 'Apply code at checkout on partner sites',
    de: 'Code an der Kasse auf Partnerseiten anwenden',
    es: 'Aplica el código en el checkout de sitios asociados'
  },
  'deals.tickets': {
    en: 'Match Tickets',
    de: 'Spieltickets',
    es: 'Entradas para Partidos'
  },
  'deals.ticketsDesc': {
    en: 'Up to 15% off group stage packages',
    de: 'Bis zu 15% Rabatt auf Gruppenphasen-Pakete',
    es: 'Hasta 15% de descuento en paquetes de fase de grupos'
  },
  'deals.findTickets': {
    en: 'Find Tickets',
    de: 'Tickets Finden',
    es: 'Encontrar Entradas'
  },
  'deals.hotels': {
    en: 'Hotels & Accommodation',
    de: 'Hotels & Unterkünfte',
    es: 'Hoteles y Alojamiento'
  },
  'deals.hotelsDesc': {
    en: 'Partner hotel deals near match venues',
    de: 'Partner-Hotelangebote in der Nähe der Spielorte',
    es: 'Ofertas de hoteles asociados cerca de los estadios'
  },
  'deals.bookHotels': {
    en: 'Book Hotels',
    de: 'Hotels Buchen',
    es: 'Reservar Hoteles'
  },
  'deals.flights': {
    en: 'Flights',
    de: 'Flüge',
    es: 'Vuelos'
  },
  'deals.flightsDesc': {
    en: 'Special fares from Frankfurt & Munich to USA',
    de: 'Sondertarife von Frankfurt & München in die USA',
    es: 'Tarifas especiales desde Frankfurt y Múnich a EE. UU.'
  },
  'deals.searchFlights': {
    en: 'Search Flights',
    de: 'Flüge Suchen',
    es: 'Buscar Vuelos'
  },
  'deals.transport': {
    en: 'Local Transport',
    de: 'Nahverkehr',
    es: 'Transporte Local'
  },
  'deals.transportDesc': {
    en: 'Shuttle services & car rentals at match cities',
    de: 'Shuttle-Services & Autovermietungen in den Spielorten',
    es: 'Servicios de transporte y alquiler de autos en las ciudades sede'
  },
  'deals.planTransport': {
    en: 'Plan Transport',
    de: 'Transport Planen',
    es: 'Planear Transporte'
  },
  // Fan HQ / Gallery
  'fanHq.label': {
    en: 'Everything You Need for Germany 2026',
    de: 'Alles was du für Deutschland 2026 brauchst',
    es: 'Todo lo que necesitas para Alemania 2026'
  },
  'fanHq.title1': {
    en: 'Fan',
    de: 'Fan',
    es: 'QG del'
  },
  'fanHq.title2': {
    en: 'HQ',
    de: 'Zentrale',
    es: 'Fan'
  },
  'fanHq.countdown': {
    en: "Countdown to Germany's First Match",
    de: 'Countdown zum ersten Spiel von Deutschland',
    es: 'Cuenta regresiva para el primer partido de Alemania'
  },
  'fanHq.days': {
    en: 'Days',
    de: 'Tage',
    es: 'Días'
  },
  'fanHq.hrs': {
    en: 'Hrs',
    de: 'Std',
    es: 'Hrs'
  },
  'fanHq.min': {
    en: 'Min',
    de: 'Min',
    es: 'Min'
  },
  'fanHq.sec': {
    en: 'Sec',
    de: 'Sek',
    es: 'Seg'
  },
  'fanHq.capacity': {
    en: 'Capacity',
    de: 'Kapazität',
    es: 'Capacidad'
  },
  'fanHq.chants': {
    en: 'Learn the',
    de: 'Lerne die',
    es: 'Aprende los'
  },
  'fanHq.chantsHighlight': {
    en: 'Chants',
    de: 'Fangesänge',
    es: 'Cánticos'
  },
  'fanHq.nationalAnthem': {
    en: 'National Anthem',
    de: 'Nationalhymne',
    es: 'Himno Nacional'
  },
  'fanHq.classicChant': {
    en: 'Classic Stadium Chant',
    de: 'Klassischer Stadiongesang',
    es: 'Cántico Clásico de Estadio'
  },
  'fanHq.crowdFavorite': {
    en: 'Crowd Favorite',
    de: 'Publikumsliebling',
    es: 'Favorito del Público'
  },
  'fanHq.victoryChant': {
    en: 'Victory Chant',
    de: 'Siegesgesang',
    es: 'Cántico de Victoria'
  },
  'fanHq.watchYoutube': {
    en: 'Watch on YouTube',
    de: 'Auf YouTube ansehen',
    es: 'Ver en YouTube'
  },
  'fanHq.phrases': {
    en: 'Essential',
    de: 'Wichtige',
    es: 'Frases'
  },
  'fanHq.phrasesHighlight': {
    en: 'Phrases',
    de: 'Sätze',
    es: 'Esenciales'
  },
  'fanHq.quickLinks': {
    en: 'Quick',
    de: 'Schnell-',
    es: 'Enlaces'
  },
  'fanHq.quickLinksHighlight': {
    en: 'Links',
    de: 'Links',
    es: 'Rápidos'
  },
  'fanHq.whereToWatch': {
    en: 'Where to Watch',
    de: 'Wo zu sehen',
    es: 'Dónde Ver'
  },
  'fanHq.whereToWatchDesc': {
    en: 'Streaming & TV channels',
    de: 'Streaming & TV-Sender',
    es: 'Canales de streaming y TV'
  },
  'fanHq.fanZones': {
    en: 'Fan Info Channel',
    de: 'Fan-Info-Kanal',
    es: 'Canal de Info para Fans'
  },
  'fanHq.fanZonesDesc': {
    en: 'Official FIFA fan information',
    de: 'Offizielle FIFA-Faninformationen',
    es: 'Información oficial FIFA para fans'
  },
  'fanHq.fifaRanking': {
    en: 'FIFA Ranking',
    de: 'FIFA-Weltrangliste',
    es: 'Ranking FIFA'
  },
  'fanHq.fifaRankingDesc': {
    en: 'Official world rankings',
    de: 'Offizielle Weltrangliste',
    es: 'Ranking mundial oficial'
  },
  'fanHq.fullSchedule': {
    en: 'Full WC Schedule',
    de: 'Kompletter WM-Spielplan',
    es: 'Calendario Completo del Mundial'
  },
  'fanHq.fullScheduleDesc': {
    en: 'All 104 matches',
    de: 'Alle 104 Spiele',
    es: 'Todos los 104 partidos'
  },
  // Footer
  'footer.desc': {
    en: 'An official Qtap by STDI digital product. Your smart NFC T-shirt hub for Germany 2026 — real-time match data, exclusive content, and travel deals at your fingertips.',
    de: 'Ein offizielles digitales Produkt von Qtap by STDI. Dein smarter NFC-T-Shirt-Hub für Deutschland 2026 — Echtzeit-Spieldaten, exklusive Inhalte und Reiseangebote direkt zur Hand.',
    es: 'Un producto digital oficial de Qtap by STDI. Tu hub de camiseta NFC inteligente para Alemania 2026 — datos de partidos en tiempo real, contenido exclusivo y ofertas de viaje al alcance de tu mano.'
  },
  'footer.resources': {
    en: 'Resources',
    de: 'Ressourcen',
    es: 'Recursos'
  },
  'footer.team': {
    en: 'Team',
    de: 'Mannschaft',
    es: 'Equipo'
  },
  'footer.firstTeam': {
    en: 'First Team',
    de: 'Erste Mannschaft',
    es: 'Primer Equipo'
  },
  'footer.matches': {
    en: 'Matches',
    de: 'Spiele',
    es: 'Partidos'
  },
  'footer.standings': {
    en: 'Standings',
    de: 'Tabelle',
    es: 'Clasificación'
  },
  'footer.travelDeals': {
    en: 'Travel Deals',
    de: 'Reiseangebote',
    es: 'Ofertas de Viaje'
  },
  'footer.support': {
    en: 'Support',
    de: 'Support',
    es: 'Soporte'
  },
  'footer.contactUs': {
    en: 'Contact Us',
    de: 'Kontaktiere uns',
    es: 'Contáctanos'
  },
  'footer.faq': {
    en: 'FAQ',
    de: 'FAQ',
    es: 'Preguntas Frecuentes'
  },
  'footer.privacy': {
    en: 'Privacy Policy',
    de: 'Datenschutzrichtlinie',
    es: 'Política de Privacidad'
  },
  'footer.terms': {
    en: 'Terms of Service',
    de: 'Nutzungsbedingungen',
    es: 'Términos de Servicio'
  },
  'footer.cookies': {
    en: 'Cookie Policy',
    de: 'Cookie-Richtlinie',
    es: 'Política de Cookies'
  },
  'footer.copyright': {
    en: 'Qtap by STDI. All rights reserved.',
    de: 'Qtap by STDI. Alle Rechte vorbehalten.',
    es: 'Qtap by STDI. Todos los derechos reservados.'
  }
};
const LanguageContext = createContext<LanguageContextType>({
  lang: 'en',
  toggleLang: () => {},
  t: (key: string) => key
});
export function LanguageProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [lang, setLang] = useState<Lang>('en');
  const toggleLang = useCallback(() => {
    setLang(prev => {
      if (prev === 'en') return 'de';
      if (prev === 'de') return 'es';
      return 'en';
    });
  }, []);
  const t = useCallback((key: string) => {
    return translations[key]?.[lang] || key;
  }, [lang]);
  return <LanguageContext.Provider value={{
    lang,
    toggleLang,
    t
  }} data-id="element-218">
      {children}
    </LanguageContext.Provider>;
}
export function useLanguage() {
  return useContext(LanguageContext);
}